/**
 * The semen analysis recorded on a sperm-freezing cycle's steps, for Home's
 * semen analysis card.
 *
 * A sperm-freezing cycle holds more than one spermiogram row. The collection
 * step writes one with no figures on the `sperm_collection` phase, and the
 * analysis step writes its own on the `fertilization` phase. Picking a row by
 * age, as the treatment hub does within one step, chose the collection row,
 * which has nothing to show, so figures she entered on the analysis step never
 * reached Home. The row is picked by what it holds instead: only rows that
 * carry a result are considered, and among those the clinic's wins, as it does
 * everywhere else.
 *
 * Kept free of hooks so the rule can be asked in a test; the Home reads the
 * cycle's phases and hands their rows in.
 */

import { hasAnalysisFigure } from "@src/packages/cycles/presenters/semenAnalysisWait";
import { spermiogramRowForDisplay } from "@src/packages/patients/hooks/spermiogramRows";
import type { SpermAnalysisResults } from "./components/SpermAnalysisSkeletonCard";

/**
 * Only what this reader looks at. A real phase spermiogram satisfies it; a test
 * does not have to hand over forty columns to say "the analysis measured 48".
 */
export type SpermiogramAnalysisRead = {
  source?: string | null;
  createdAt?: string | Date | null;
  spermCount?: number | null;
  spermConcentration?: number | null;
  semenVolume?: number | null;
  volumeUnit?: string | null;
  tmsc?: number | null;
  motility?: number | null;
  progressiveMotility?: number | null;
  normalMorphology?: number | null;
};

const figure = (value: unknown): string | undefined =>
  typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : undefined;

/**
 * The analysis on the cycle's spermiograms, as the results card wants it, or
 * null when no row carries a figure the card shows.
 *
 * Values come back as bare numbers where the card supplies the unit itself;
 * the volume keeps its unit when the record measured in anything but
 * millilitres, because a lab that measured in cc must not be relabelled.
 */
export function semenAnalysisResultsFromSpermiograms(
  rows: readonly SpermiogramAnalysisRead[] | undefined | null,
): SpermAnalysisResults | null {
  const row = spermiogramRowForDisplay((rows ?? []).filter(hasAnalysisFigure));
  if (!row) return null;

  const mapped: SpermAnalysisResults = {};
  const volume = figure(row.semenVolume);
  if (volume) {
    const unit = row.volumeUnit?.trim();
    mapped.volume =
      unit && unit.toLowerCase() !== "ml" ? `${volume} ${unit}` : volume;
  }
  const concentration = figure(row.spermConcentration);
  if (concentration) mapped.concentration = concentration;
  const progressive = figure(row.progressiveMotility);
  if (progressive) mapped.progressiveMotility = progressive;
  const morphology = figure(row.normalMorphology);
  if (morphology) mapped.normalMorphology = morphology;

  return Object.keys(mapped).length > 0 ? mapped : null;
}
